import { Fragment, useState } from "react";
import {
  Stepper,
  Step,
  StepLabel,
  StepButton,
  Button,
  Box,
  Typography,
  Container,
  Card,
  CardContent,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Breadcrumb from "../../common/breadcrumb";
import { ToastContainer, toast } from "react-toastify";
import CommonLoader from "../../common/commonLoader";
import { useEffect, useRef } from "react";
import {
  getVendorEvaluationById,
  saveOrUpdateVendorEvaluation,
  checkVendorEvaluationPanNoExists,
  uploadVendorEvaluationFile,
} from "../../api/ap_services";
import { ArrowBack } from "@mui/icons-material";
import {
  Step1FactoryEvaluation,
  Step2ManufacturingFacility,
  Step3AddressInfo,
  Step4AdditionalDetails,
  Step5Review,
} from "./steps";
import { useNavigate, useParams } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(3),
  },
  stepContent: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(3),
    paddingTop: theme.spacing(1), // Reduced top padding since stepper is sticky
  },
  card: {
    padding: theme.spacing(2),
  },
  stickyStepper: {
    position: "sticky",
    // top: 0,
    top: "5rem",
    zIndex: 5, //1000,
    backgroundColor: "#fff",
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(2),
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
}));

const steps = [
  "Factory Details",
  "Manufacturing Facility",
  "Evaluation",
  "Annexure Digital Images",
  "Review & Submit",
];

/** Normalize API date to YYYY-MM-DD for date inputs */
const formatDateForInput = (val) => {
  if (val == null || val === "") return "";
  if (typeof val !== "string") return String(val);
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return val;
    return d.toISOString().slice(0, 10);
  } catch {
    return val;
  }
};

/**
 * Builds machineryAvailableDetailsJson array from form values (machineryDesc_1..5, machineryNo_1..5, machineryStatus_1..5).
 * Format: [{ description, no, maintenanceStatus }, ...] — only rows with at least one value included.
 */
const buildMachineryAvailableDetailsJson = (values) => {
  const arr = [];
  for (let idx = 1; idx <= 5; idx++) {
    const description = values[`machineryDesc_${idx}`];
    const no = values[`machineryNo_${idx}`];
    const maintenanceStatus = values[`machineryStatus_${idx}`];
    const desc =
      description != null && String(description).trim() !== ""
        ? String(description).trim()
        : "";
    const num = no != null && String(no).trim() !== "" ? String(no).trim() : "";
    const status =
      maintenanceStatus != null && String(maintenanceStatus).trim() !== ""
        ? String(maintenanceStatus).trim()
        : "";
    if (desc !== "" || num !== "" || status !== "") {
      arr.push({
        description: desc || "",
        no: num || "",
        maintenanceStatus: status || "",
      });
    }
  }
  return arr;
};

/**
 * Maps API response (getAllVendorEvaluation / getVendorEvaluationById) to form field names
 * so stored data populates the form for editing.
 */
const mapApiResponseToFormValues = (apiObj, defaults) => {
  const raw = Array.isArray(apiObj) ? apiObj[0] : apiObj;
  if (!raw || typeof raw !== "object") return defaults;
  const d = { ...defaults };

  const get = (key) => raw[key];
  const setForm = (formKey, value) => {
    if (value !== undefined && value !== null && value !== "")
      d[formKey] = value;
  };

  setForm("piNo", get("piNo") ?? get("pNo") ?? get("pno"));
  setForm("factoryDate", formatDateForInput(get("factoryDate")));
  setForm("factoryName", get("factoryName"));
  setForm("factoryAddress", get("factoryAddress"));
  setForm("factoryInchargeName", get("factoryInchargeName"));
  setForm("factoryInchargeTitle", get("factoryInchargeTitle"));
  setForm("factoryPhone", get("factoryPhone"));
  setForm("factoryEmail", get("factoryEmail"));
  setForm("factoryYearOfEstablishment", get("factoryYearOfEstablishment"));
  setForm("factorySize", get("factorySize"));
  setForm("category", Array.isArray(get("category")) ? get("category") : []);
  setForm("categoryOtherRemarks", get("categoryOtherRemarks"));
  setForm("bankName", get("bankName"));
  setForm("accountNo", get("accountNo"));
  setForm("ifscCode", get("ifscCode"));
  setForm("panNo", get("panNo"));
  setForm("ownerName", get("ownerName"));
  setForm("ownerTitle", get("ownerTitle"));
  setForm("ownerAddress", get("ownerAddress"));
  setForm("ownerContactNo", get("ownerContactNo"));
  setForm("ownerEmail", get("ownerEmail"));
  setForm("existingJobWorkVendor", get("existingJobWorkVendor"));
  setForm(
    "existingJobWorkVendorYesRemarks",
    get("existingJobWorkVendorYesRemarks"),
  );
  setForm("referredByName", get("referredByName"));
  setForm("referredByTitle", get("referredByTitle"));
  setForm("referredByMobileNo", get("referredByMobileNo"));
  setForm("referenceEmpId", get("referenceEmpId"));
  setForm("referredByAddress", get("referredByAddress"));
  setForm(
    "referredFor",
    Array.isArray(get("referredFor")) ? get("referredFor") : [],
  );
  setForm("referredForOtherRemarks", get("referredForOtherRemarks"));
  setForm("referenceFeedbackAboutVendor", get("referenceFeedbackAboutVendor"));
  setForm(
    "isExistingUser",
    get("isExistingUser") != null ? get("isExistingUser") : d.isExistingUser,
  );
  const vendorTypeIdVal = get("vendorTypeId") ?? get("vendor_type_id");
  const vendorCodeVal = get("vendorCode") ?? get("vendor_code");
  if (vendorTypeIdVal != null && vendorTypeIdVal !== "") {
    setForm("vendorTypeId", vendorTypeIdVal);
  }
  setForm("vendorCode", vendorCodeVal);
  if (vendorCodeVal != null && vendorCodeVal !== "") {
    setForm("vendorCodeOption", {
      vendorTypeId: vendorTypeIdVal,
      code: vendorCodeVal,
      name: get("vendorName") ?? get("vendor_name") ?? "",
    });
  }

  setForm("infrastructureAvailable", get("infrastructureAvailable"));
  setForm("totalSquareFeetDetails", get("totalSquareFeetDetails"));
  setForm("totalBuildUpAreaDetails", get("totalBuildUpAreaDetails"));
  setForm("totalVacantLandDetails", get("totalVacantLandDetails"));
  setForm("roadAccess", get("roadAccess"));
  setForm("machineryAvailable", get("machineryAvailable"));
  const machineryJson = get("machineryAvailableDetailsJson");
  if (machineryJson != null) {
    const arr =
      typeof machineryJson === "string"
        ? (() => {
            try {
              return JSON.parse(machineryJson);
            } catch {
              return [];
            }
          })()
        : Array.isArray(machineryJson)
          ? machineryJson
          : [];
    arr.forEach((row, i) => {
      const idx = i + 1;
      if (idx <= 5) {
        setForm(`machineryDesc_${idx}`, row.description);
        setForm(`machineryNo_${idx}`, row.no != null ? String(row.no) : "");
        setForm(`machineryStatus_${idx}`, row.maintenanceStatus);
      }
    });
  }
  setForm(
    "adequateKnowledgeManufacturingProcess",
    get("adequateKnowledgeManufacturingProcess"),
  );
  setForm("adequateKnowledgeRemarks", get("adequateKnowledgeRemarks"));
  setForm(
    "recordOfRawmaterialsMaintained",
    get("recordOfRawmaterialsMaintained"),
  );
  setForm(
    "rawmaterialsMaintainedRemarks",
    get("rawmaterialsMaintainedRemarks"),
  );
  setForm("trainedManpower", get("trainedManpower"));
  setForm("trainedManpowerRemarks", get("trainedManpowerRemarks"));
  setForm("workingAreaDesigned", get("workingAreaDesigned"));
  setForm("workingAreaDesignedRemarks", get("workingAreaDesignedRemarks"));
  setForm("workstationsDesigned", get("workstationsDesigned"));
  setForm("workstationsDesignedRemarks", get("workstationsDesignedRemarks"));
  setForm("spaceForEachWorkstation", get("spaceForEachWorkstation"));
  setForm("lightingArrangements", get("lightingArrangements"));
  setForm("airVentilation", get("airVentilation"));
  setForm("factoryCleanliness", get("factoryCleanliness"));
  setForm("rawMaterialStorage", get("rawMaterialStorage"));
  setForm("machinerySafetyGuards", get("machinerySafetyGuards"));
  setForm("materialHandling", get("materialHandling"));
  setForm("packingAndDispatch", get("packingAndDispatch"));
  setForm("storageCapacity", get("storageCapacity"));
  setForm("chemicalHazardControl", get("chemicalHazardControl"));
  setForm("electricityHazardControl", get("electricityHazardControl"));
  setForm("powerSupplyOptions", get("powerSupplyOptions"));
  setForm("effluentTreatmentPlan", get("effluentTreatmentPlan"));
  setForm("qualityPolicySop", get("qualityPolicySop"));
  setForm("qualityPolicyDisplayed", get("qualityPolicyDisplayed"));
  setForm("randomQualityCheck", get("randomQualityCheck"));
  setForm("inspectionArea", get("inspectionArea"));
  setForm("availabilityOfQcQa", get("availabilityOfQcQa"));
  setForm("testingLabFacilities", get("testingLabFacilities"));
  setForm("minimumAgeLimit", get("minimumAgeLimit"));
  setForm("staffHeadCount", get("staffHeadCount"));
  setForm("labourHeadCount", get("labourHeadCount"));
  setForm("totalHeadCount", get("totalHeadCount"));
  setForm("permanentHeadCount", get("permanentHeadCount"));
  setForm("contractHeadCount", get("contractHeadCount"));
  setForm("temperoryHeadCount", get("temperoryHeadCount"));
  setForm("standardWorkingHours", get("standardWorkingHours"));
  setForm("shiftsPerDay", get("shiftsPerDay"));
  setForm("systemOfPayment", get("systemOfPayment"));
  setForm("workersGetPaid", get("workersGetPaid"));
  setForm("paidSickAndAnnualLeave", get("paidSickAndAnnualLeave"));
  setForm("accomadation", get("accomadation"));
  setForm("noOfMigrantWorkers", get("noOfMigrantWorkers"));
  setForm("factoryLicenceNumber", get("factoryLicenceNumber"));
  setForm("factoryLicenceNumberRemarks", get("factoryLicenceNumberRemarks"));
  setForm("employeesEnrolledEpf", get("employeesEnrolledEpf"));
  setForm("employeesEnrolledEpfRemarks", get("employeesEnrolledEpfRemarks"));
  setForm("employeesEnrolledEsi", get("employeesEnrolledEsi"));
  setForm("employeesEnrolledEsiRemarks", get("employeesEnrolledEsiRemarks"));
  setForm("healthAndSafety", get("healthAndSafety"));
  setForm("healthAndSafetyRemarks", get("healthAndSafetyRemarks"));
  setForm(
    "availabilityFirefightingEquipment",
    get("availabilityFirefightingEquipment"),
  );
  setForm(
    "availabilityFirefightingEquipmentRemarks",
    get("availabilityFirefightingEquipmentRemarks"),
  );
  setForm("fireAlarmAudible", get("fireAlarmAudible"));
  setForm("fireAlarmAudibleRemarks", get("fireAlarmAudibleRemarks"));
  setForm("emergencyExit", get("emergencyExit"));
  setForm("emergencyExitRemarks", get("emergencyExitRemarks"));
  setForm("financialyStrongEnough", get("financialyStrongEnough"));
  setForm(
    "financialyStrongEnoughRemarks",
    get("financialyStrongEnoughRemarks"),
  );
  setForm("maintainBankAccount", get("maintainBankAccount"));
  setForm("maintainBankAccountRemarks", get("maintainBankAccountRemarks"));
  setForm("extendSupplyOnCredit", get("extendSupplyOnCredit"));
  setForm("extendSupplyOnCreditRemarks", get("extendSupplyOnCreditRemarks"));
  setForm("overallComments", get("overallComments"));
  setForm("qualityTeamName", get("qualityTeamName"));
  setForm("qualityTeamSignature", get("qualityTeamSignature"));
  setForm("hrTeamName", get("hrTeamName"));
  setForm("hrTeamSignature", get("hrTeamSignature"));
  setForm("financeTeamName", get("financeTeamName"));
  setForm("financeTeamSignature", get("financeTeamSignature"));
  setForm("headManufacturing", get("headManufacturing"));
  setForm("headManufacturingSignature", get("headManufacturingSignature"));
  setForm("approvedDate", formatDateForInput(get("approvedDate")));
  setForm("rentedBuilding", get("rentedBuilding"));
  setForm("rentedBuildingRemarks", get("rentedBuildingRemarks"));
  setForm("ownBuildingOwnership", get("ownBuildingOwnership"));
  setForm("ownBuildingOwnershipRemarks", get("ownBuildingOwnershipRemarks"));
  setForm(
    "companyRegistrationCertificate",
    get("companyRegistrationCertificate"),
  );
  setForm(
    "companyRegistrationCertificateRemarks",
    get("companyRegistrationCertificateRemarks"),
  );
  setForm("factoryLicence", get("factoryLicence"));
  setForm("factoryLicenceRemarks", get("factoryLicenceRemarks"));
  setForm("fireCertificate", get("fireCertificate"));
  setForm("fireCertificateRemarks", get("fireCertificateRemarks"));
  setForm("epfCodeRegistration", get("epfCodeRegistration"));
  setForm("epfCodeRegistrationRemarks", get("epfCodeRegistrationRemarks"));
  setForm("esiCodeRegistration", get("esiCodeRegistration"));
  setForm("esiCodeRegistrationRemarks", get("esiCodeRegistrationRemarks"));
  setForm("panCard", get("panCard"));
  setForm("panCardRemarks", get("panCardRemarks"));
  setForm("aadharCard", get("aadharCard"));
  setForm("aadharCardRemarks", get("aadharCardRemarks"));
  setForm("factoryBankDetails", get("factoryBankDetails"));
  setForm("factoryBankDetailsRemarks", get("factoryBankDetailsRemarks"));
  setForm("listOfMachinery", get("listOfMachinery"));
  setForm("listOfMachineryRemarks", get("listOfMachineryRemarks"));
  setForm("polutionCentrolBoard", get("polutionCentrolBoard"));
  setForm("polutionCentrolBoardRemarks", get("polutionCentrolBoardRemarks"));
  //   setForm(
  //   "machineryAvailableDetailsJson",
  //   get("machineryAvailableDetailsJson")
  // );

  return d;
};

const initialValues = {
  // Step 1: Evaluation Form / Vendor Selection
  isExistingUser: "N",
  vendorTypeId: null,
  vendorCode: null,
  vendorCodeOption: null,
  piNo: "",
  factoryDate: "",
  factoryName: "",
  factoryAddress: "",
  factoryInchargeName: "",
  factoryInchargeTitle: "",
  factoryPhone: "",
  factoryEmail: "",
  factoryYearOfEstablishment: "",
  factorySize: "",
  category: [],
  categoryOtherRemarks: "",
  bankName: "",
  accountNo: "",
  ifscCode: "",
  panNo: "",

  ownerName: "",
  ownerTitle: "",
  ownerAddress: "",
  ownerContactNo: "",
  ownerEmail: "",

  existingJobWorkVendor: "",
  existingJobWorkVendorYesRemarks: "",

  referredByName: "",
  referredByTitle: "",
  referredByMobileNo: "",
  referenceEmpId: "",
  referredByAddress: "",
  referredFor: [],
  referredForOtherRemarks: "",
  referenceFeedbackAboutVendor: "",

  // Step 2: Manufacturing Facility (Pages 2, 3, 4)
  // Page 2 - Infrastructure
  infrastructureAvailable: "",
  totalSquareFeetDetails: "",
  totalBuildUpAreaDetails: "",
  totalVacantLandDetails: "",
  roadAccess: "",
  // Page 2 - Machinery
  machineryAvailable: "",
  // machineryAvailableDetailsJson: "",
  // Page 2 - Knowledge, Records, Manpower, Process Flow
  adequateKnowledgeManufacturingProcess: "",
  adequateKnowledgeRemarks: "",
  recordOfRawmaterialsMaintained: "",
  rawmaterialsMaintainedRemarks: "",
  trainedManpower: "",
  trainedManpowerRemarks: "",
  workingAreaDesigned: "",
  workingAreaDesignedRemarks: "",
  // Page 3 - Workstations
  workstationsDesigned: "",
  workstationsDesignedRemarks: "",
  // Page 3 - Sufficiency (13 parameters)
  spaceForEachWorkstation: "",
  lightingArrangements: "",
  airVentilation: "",
  factoryCleanliness: "",
  rawMaterialStorage: "",
  machinerySafetyGuards: "",
  materialHandling: "",
  packingAndDispatch: "",
  storageCapacity: "",
  chemicalHazardControl: "",
  electricityHazardControl: "",
  powerSupplyOptions: "",
  effluentTreatmentPlan: "",
  // Page 3 - Quality Management (6 parameters)
  qualityPolicySop: "",
  qualityPolicyDisplayed: "",
  randomQualityCheck: "",
  inspectionArea: "",
  availabilityOfQcQa: "",
  testingLabFacilities: "",
  // Page 4 - HR Management
  minimumAgeLimit: "",
  staffHeadCount: "",
  labourHeadCount: "",
  totalHeadCount: "",
  permanentHeadCount: "",
  contractHeadCount: "",
  temperoryHeadCount: "",
  standardWorkingHours: "",
  shiftsPerDay: "",
  systemOfPayment: "",
  workersGetPaid: "",
  paidSickAndAnnualLeave: "",
  accomadation: "",
  noOfMigrantWorkers: "",
  // Page 4 - Statutory Compliance
  factoryLicenceNumber: "",
  factoryLicenceNumberRemarks: "",
  employeesEnrolledEpf: "",
  employeesEnrolledEpfRemarks: "",
  employeesEnrolledEsi: "",
  employeesEnrolledEsiRemarks: "",
  // Page 4 - Health and Safety
  healthAndSafety: "",
  healthAndSafetyRemarks: "",
  availabilityFirefightingEquipment: "",
  availabilityFirefightingEquipmentRemarks: "",
  fireAlarmAudible: "",
  fireAlarmAudibleRemarks: "",
  emergencyExit: "",
  emergencyExitRemarks: "",

  // Page 5 - Vendor Verification
  // vendorVerificationComment: "",

  // Page 5 - Financial Strength
  financialyStrongEnough: "",
  financialyStrongEnoughRemarks: "",
  maintainBankAccount: "",
  maintainBankAccountRemarks: "",
  extendSupplyOnCredit: "",
  extendSupplyOnCreditRemarks: "",

  // Page 5 - Overall Comments

  // Page 5 - Evaluation done by
  qualityTeamName: "",
  qualityTeamSignature: "",
  hrTeamName: "",
  hrTeamSignature: "",
  financeTeamName: "",
  financeTeamSignature: "",

  // Page 5 - Approved as Supplier by
  headManufacturing: "",
  headManufacturingSignature: "",
  approvedDate: "",

  // Page 6 - Documents checklist
  rentedBuilding: "",
  rentedBuildingRemarks: "",
  ownBuildingOwnership: "",
  ownBuildingOwnershipRemarks: "",
  companyRegistrationCertificate: "",
  companyRegistrationCertificateRemarks: "",
  factoryLicence: "",
  factoryLicenceRemarks: "",
  fireCertificate: "",
  fireCertificateRemarks: "",
  epfCodeRegistration: "",
  epfCodeRegistrationRemarks: "",
  esiCodeRegistration: "",
  esiCodeRegistrationRemarks: "",
  panCard: "",
  panCardRemarks: "",
  aadharCard: "",
  aadharCardRemarks: "",
  factoryBankDetails: "",
  factoryBankDetailsRemarks: "",
  listOfMachinery: "",
  listOfMachineryRemarks: "",
  polutionCentrolBoard: "",
  polutionCentrolBoardRemarks: "",
  // Document attachments (when Yes is selected)
  rentalCopy_attachment: null,
  ownershipDetails_attachment: null,
  companyRegistration_attachment: null,
  factoryLicenceDoc_attachment: null,
  fireCertificate_attachment: null,
  epfRegistration_attachment: null,
  esiRegistration_attachment: null,
  panCard_attachment: null,
  udyogAadhar_attachment: null,
  factoryBankDetails_attachment: null,
  listOfMachinery_attachment: null,
  polutionControlBoard_attachment: null,

  // Digital Images - Annexure 1 (18 images)
  factoryNameBoard: null,
  factoryPictureFull: null,
  factoryEntrance: null,
  productionFloorPic1: null,
  productionFloorPic2: null,
  productionFloorPic3: null,
  productionFloorPic4: null,
  productionFloorPic5: null,
  productionFloorPic6: null,
  storageSectionPic1: null,
  storageSectionPic2: null,
  storageSectionPic3: null,
  firefightingEquipmentPic1: null,
  firefightingEquipmentPic2: null,
  firefightingEquipmentPic3: null,
  othersPic1: null,
  othersPic2: null,
  othersPic3: null,

  overallComments: "",
};

// Safe transform: avoid val.trim on non-string (prevents "trim is not a function")
const safeString = () =>
  Yup.string().transform((v) =>
    v != null && typeof v === "string" ? v.trim() : v == null ? "" : String(v),
  );

const validationSchema = Yup.object({
  // Step 1 - Factory details
  factoryName: safeString().required("Factory Name is required"),
  factoryAddress: safeString().required("Factory Address is required"),
  factoryInchargeName: safeString().required(
    "Factory Manager / In-charge Name is required",
  ),
  factoryPhone: safeString().required("Factory Telephone is required"),
  factoryEmail: safeString()
    .email("Invalid email")
    .required("Factory E-mail ID is required"),
  factoryYearOfEstablishment: Yup.string()
    .trim()
    .required("Year of Establishment is required"),
  factorySize: Yup.string().trim().required("Facility Size is required"),
  panNo: Yup.string().trim().required("PAN Number is required"),

  category: Yup.array()
    .min(1, "Select at least one category")
    .required("Category is required"),
  categoryOtherRemarks: Yup.string().when("category", {
    is: (val) => Array.isArray(val) && val.includes("Other"),
    then: (schema) =>
      schema.trim().required("Please provide description for Other"),
    otherwise: (schema) => schema.nullable(),
  }),

  ownerName: Yup.string().trim().required("Occupier Name is required"),
  ownerAddress: Yup.string().trim().required("Occupier Address is required"),
  ownerContactNo: Yup.string()
    .trim()
    .required("Occupier Contact Number is required"),
  ownerEmail: Yup.string()
    .trim()
    .email("Invalid email")
    .required("Occupier E-mail ID is required"),

  existingJobWorkVendor: Yup.string()
    .oneOf(["Y", "N"], "Select Yes or No")
    .required("This field is required"),
  existingJobWorkVendorYesRemarks: Yup.string()
    .transform((v) =>
      v != null && typeof v === "string"
        ? v.trim()
        : v == null
          ? ""
          : String(v),
    )
    .when("existingJobWorkVendor", (val, schema) =>
      val === "Y"
        ? schema.required("Brands Worked With is required")
        : schema.nullable(),
    ),

  referredByName: Yup.string()
    .trim()
    .required("Referred By - Name is required"),
  referredByMobileNo: Yup.string()
    .trim()
    .required("Referred By - Mobile No. is required"),
  referredByAddress: Yup.string()
    .trim()
    .required("Referred By - Address is required"),
  referredFor: Yup.array()
    .min(1, "Select at least one option for Referred for")
    .required("Referred For is required"),
  referredForOtherRemarks: Yup.string().when("referredFor", {
    is: (val) => Array.isArray(val) && val.includes("Other"),
    then: (schema) =>
      schema.trim().required("Please provide description for Other"),
    otherwise: (schema) => schema.nullable(),
  }),

  // Step 2 - key mandatory fields
  infrastructureAvailable: Yup.string()
    .oneOf(["Y", "N"], "Select Yes or No")
    .required("Infrastructure Available is required"),
  totalSquareFeetDetails: Yup.string()
    .transform((v) =>
      v != null && typeof v === "string"
        ? v.trim()
        : v == null
          ? ""
          : String(v),
    )
    .when("infrastructureAvailable", (val, schema) =>
      val === "Y"
        ? schema.required("Total square feet is required")
        : schema.nullable(),
    ),
  totalBuildUpAreaDetails: Yup.string()
    .transform((v) =>
      v != null && typeof v === "string"
        ? v.trim()
        : v == null
          ? ""
          : String(v),
    )
    .when("infrastructureAvailable", (val, schema) =>
      val === "Y"
        ? schema.required("Total build up area is required")
        : schema.nullable(),
    ),
  totalVacantLandDetails: Yup.string()
    .transform((v) =>
      v != null && typeof v === "string"
        ? v.trim()
        : v == null
          ? ""
          : String(v),
    )
    .when("infrastructureAvailable", (val, schema) =>
      val === "Y"
        ? schema.required("Total vacant land is required")
        : schema.nullable(),
    ),
  roadAccess: Yup.string()
    .transform((v) =>
      v != null && typeof v === "string"
        ? v.trim()
        : v == null
          ? ""
          : String(v),
    )
    .when("infrastructureAvailable", (val, schema) =>
      val === "Y"
        ? schema.required("Road access is required")
        : schema.nullable(),
    ),

  machineryAvailable: Yup.string()
    .oneOf(["Y", "N"], "Select Yes or No")
    .required("Machinery Available is required"),

  adequateKnowledgeManufacturingProcess: Yup.string()
    .oneOf(["Y", "N"], "Select Yes or No")
    .required("Knowledge field is required"),

  recordOfRawmaterialsMaintained: Yup.string()
    .oneOf(["Y", "N"], "Select Yes or No")
    .required("Raw Material Records field is required"),

  trainedManpower: Yup.string()
    .oneOf(["Y", "N"], "Select Yes or No")
    .required("Trained Manpower field is required"),

  workingAreaDesigned: Yup.string()
    .oneOf(["Y", "N"], "Select Yes or No")
    .required("Working Area Designed field is required"),

  workstationsDesigned: Yup.string()
    .oneOf(["Y", "N"], "Select Yes or No")
    .required("Workstations Designed field is required"),

  minimumAgeLimit: safeString().required("Minimum Age Limit is required"),
  staffHeadCount: safeString().required("Staff Head Count is required"),
  labourHeadCount: safeString().required("Labour Head Count is required"),
  totalHeadCount: safeString().required("Total Head Count is required"),

  factoryLicenceNumber: Yup.string()
    .oneOf(["Y", "N"], "Select Yes or No")
    .required("Factory Licence is required"),
  factoryLicenceNumberRemarks: Yup.string()
    .transform((v) =>
      v != null && typeof v === "string"
        ? v.trim()
        : v == null
          ? ""
          : String(v),
    )
    .when("factoryLicenceNumber", (val, schema) =>
      val === "Y"
        ? schema.required("Factory Licence Details are required")
        : schema.nullable(),
    ),
  employeesEnrolledEpf: Yup.string()
    .oneOf(["Y", "N"], "Select Yes or No")
    .required("EPF Enrollment is required"),
  employeesEnrolledEpfRemarks: Yup.string()
    .transform((v) =>
      v != null && typeof v === "string"
        ? v.trim()
        : v == null
          ? ""
          : String(v),
    )
    .when("employeesEnrolledEpf", (val, schema) =>
      val === "Y"
        ? schema.required("EPF Enrollment Details are required")
        : schema.nullable(),
    ),
  employeesEnrolledEsi: Yup.string()
    .oneOf(["Y", "N"], "Select Yes or No")
    .required("ESI Enrollment is required"),
  employeesEnrolledEsiRemarks: Yup.string()
    .transform((v) =>
      v != null && typeof v === "string"
        ? v.trim()
        : v == null
          ? ""
          : String(v),
    )
    .when("employeesEnrolledEsi", (val, schema) =>
      val === "Y"
        ? schema.required("ESI Enrollment Details are required")
        : schema.nullable(),
    ),

  healthAndSafety: Yup.string()
    .oneOf(["Y", "N"], "Select Yes or No")
    .required("Health Safety Policy is required"),
  healthAndSafetyRemarks: Yup.string()
    .transform((v) =>
      v != null && typeof v === "string"
        ? v.trim()
        : v == null
          ? ""
          : String(v),
    )
    .when("healthAndSafety", (val, schema) =>
      val === "Y"
        ? schema.required("Health Safety Policy Details are required")
        : schema.nullable(),
    ),
  availabilityFirefightingEquipment: Yup.string()
    .oneOf(["Y", "N"], "Select Yes or No")
    .required("Firefighting Equipment is required"),
  availabilityFirefightingEquipmentRemarks: Yup.string()
    .transform((v) =>
      v != null && typeof v === "string"
        ? v.trim()
        : v == null
          ? ""
          : String(v),
    )
    .when("availabilityFirefightingEquipment", (val, schema) =>
      val === "Y"
        ? schema.required("Firefighting Equipment Details are required")
        : schema.nullable(),
    ),
  fireAlarmAudible: Yup.string()
    .oneOf(["Y", "N"], "Select Yes or No")
    .required("Fire Alarm is required"),
  fireAlarmAudibleRemarks: Yup.string()
    .transform((v) =>
      v != null && typeof v === "string"
        ? v.trim()
        : v == null
          ? ""
          : String(v),
    )
    .when("fireAlarmAudible", (val, schema) =>
      val === "Y"
        ? schema.required("Fire Alarm Details are required")
        : schema.nullable(),
    ),
  emergencyExit: Yup.string()
    .oneOf(["Y", "N"], "Select Yes or No")
    .required("Emergency Exits is required"),
  emergencyExitRemarks: Yup.string()
    .transform((v) =>
      v != null && typeof v === "string"
        ? v.trim()
        : v == null
          ? ""
          : String(v),
    )
    .when("emergencyExit", (val, schema) =>
      val === "Y"
        ? schema.required("Emergency Exits Details are required")
        : schema.nullable(),
    ),
});

/**
 * Validation schema with async PAN uniqueness check against vendor evaluation table.
 * When editing, pass current vendorEvaluationId and originalPanNo; if PAN unchanged, API is skipped.
 */
const getValidationSchema = (originalPanNo) =>
  validationSchema.shape({
    panNo: Yup.string()
      .trim()
      .required("PAN Number is required")
      .test("pan-exists", "Pan No already exist", async (value) => {
        if (!value || typeof value !== "string" || value.trim() === "")
          return true;
        const valueTrimmed = value.trim();
        const originalTrimmed =
          originalPanNo != null ? String(originalPanNo).trim() : "";
        if (originalTrimmed !== "" && valueTrimmed === originalTrimmed) {
          return true;
        }
        try {
          const res = await checkVendorEvaluationPanNoExists(valueTrimmed);
          return !res.exists;
        } catch {
          return true;
        }
      }),
  });

const VendorEvaluationForm = () => {
  const isFinanceTeam =
    JSON.parse(sessionStorage.getItem("userData")).groupId === 7;
  const isHRTeam = JSON.parse(sessionStorage.getItem("userData")).groupId === 2;
  const isQCTeam = JSON.parse(sessionStorage.getItem("userData")).groupId === 3;
  const isAdmin = JSON.parse(sessionStorage.getItem("userData")).groupId === 1;

  const navigate = useNavigate();
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [maxStepReached, setMaxStepReached] = useState(0);
  const { vendorEvaluationId: vendorEvaluationListId } = useParams();
  const isEdit = Boolean(vendorEvaluationListId);
  const [loading, setLoading] = useState(!!vendorEvaluationListId);
  const stepperRef = useRef(null);
  const [selectedForEdit, setSelectedForEdit] = useState(null);
  const [vendorEvaluationId, setVendorEvaluationId] = useState(
    isEdit ? vendorEvaluationListId : null,
  );

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "instant" });
    }, 0);
  }, [activeStep]);

  useEffect(() => {
    if (vendorEvaluationListId != null && vendorEvaluationListId !== "") {
      setLoading(true);
      getVendorEvaluationById(vendorEvaluationListId)
        .then((res) => {
          setLoading(false);
          const raw = res?.response?.responseObject ?? res?.response;
          const data = Array.isArray(raw) ? raw[0] : raw;
          const hasValidRecord =
            data &&
            typeof data === "object" &&
            (data.vendorEvaluationId != null || data.id != null);
          if (hasValidRecord) {
            if (typeof data.category === "string")
              data.category = data.category.split(",");
            if (typeof data.referredFor === "string")
              data.referredFor = data.referredFor.split(",");
            setSelectedForEdit(data);
            const id = data.vendorEvaluationId ?? data.id;
            if (id != null) setVendorEvaluationId(id);
            const savedStep =
              data.currentStep ?? data.activeStep ?? data.current_step ?? 0;
            const stepNum = Math.min(
              Math.max(0, Number(savedStep)),
              steps.length - 1,
            );
            if (!Number.isNaN(stepNum)) {
              setActiveStep(stepNum);
              setMaxStepReached(stepNum);
            }
          } else {
            setSelectedForEdit(null);
          }
        })
        .catch(() => {
          setLoading(false);
          setSelectedForEdit(null);
        });
    } else {
      setSelectedForEdit(null);
    }
  }, [vendorEvaluationListId]);

  const stepFieldGroups = [
    // Step 0 - Factory details sections
    [
      "isExistingUser",
      "vendorTypeId",
      "vendorCode",
      "vendorCodeOption",
      "piNo",
      "factoryDate",
      "factoryName",
      "factoryAddress",
      "factoryInchargeName",
      "factoryInchargeTitle",
      "factoryPhone",
      "factoryEmail",
      "factoryYearOfEstablishment",
      "factorySize",
      "category",
      "categoryOtherRemarks",
      "bankName",
      "accountNo",
      "ifscCode",
      "panNo",
      "ownerName",
      "ownerTitle",
      "ownerAddress",
      "ownerContactNo",
      "ownerEmail",
      "existingJobWorkVendor",
      "existingJobWorkVendorYesRemarks",
      "referredByName",
      "referredByTitle",
      "referredByMobileNo",
      "referenceEmpId",
      "referredByAddress",
      "referredFor",
      "referredForOtherRemarks",
      "referenceFeedbackAboutVendor",
    ],
    // Step 1 - Manufacturing facility (core required fields + Page 5 & 6)
    [
      "infrastructureAvailable",
      "totalSquareFeetDetails",
      "totalBuildUpAreaDetails",
      "totalVacantLandDetails",
      "roadAccess",
      "machineryAvailable",
      "adequateKnowledgeManufacturingProcess",
      "adequateKnowledgeRemarks",
      "recordOfRawmaterialsMaintained",
      "rawmaterialsMaintainedRemarks",
      "trainedManpower",
      "trainedManpowerRemarks",
      "workingAreaDesigned",
      "workingAreaDesignedRemarks",
      "workstationsDesigned",
      "workstationsDesignedRemarks",
      "minimumAgeLimit",
      "staffHeadCount",
      "labourHeadCount",
      "totalHeadCount",
      "permanentHeadCount",
      "contractHeadCount",
      "temperoryHeadCount",
      "standardWorkingHours",
      "shiftsPerDay",
      "systemOfPayment",
      "workersGetPaid",
      "paidSickAndAnnualLeave",
      "accomadation",
      "noOfMigrantWorkers",
      "factoryLicenceNumber",
      "factoryLicenceNumberRemarks",
      "employeesEnrolledEpf",
      "employeesEnrolledEpfRemarks",
      "employeesEnrolledEsi",
      "employeesEnrolledEsiRemarks",
      "healthAndSafety",
      "healthAndSafetyRemarks",
      "availabilityFirefightingEquipment",
      "availabilityFirefightingEquipmentRemarks",
      "fireAlarmAudible",
      "fireAlarmAudibleRemarks",
      "emergencyExit",
      "emergencyExitRemarks",
      "spaceForEachWorkstation",
      "lightingArrangements",
      "airVentilation",
      "factoryCleanliness",
      "rawMaterialStorage",
      "machinerySafetyGuards",
      "materialHandling",
      "packingAndDispatch",
      "storageCapacity",
      "chemicalHazardControl",
      "electricityHazardControl",
      "powerSupplyOptions",
      "effluentTreatmentPlan",
      // QUALITY
      "qualityPolicySop",
      "qualityPolicyDisplayed",
      "randomQualityCheck",
      "inspectionArea",
      "availabilityOfQcQa",
      "testingLabFacilities",
    ],
    // Step 2 - Evaluation (Page 5 & 6 fields)
    [
      // Page 5
      // "vendorVerificationComment",
      "financialyStrongEnough",
      "financialyStrongEnoughRemarks",
      "maintainBankAccount",
      "maintainBankAccountRemarks",
      "extendSupplyOnCredit",
      "extendSupplyOnCreditRemarks",
      "overallComments",
      "qualityTeamName",
      "qualityTeamSignature",
      "hrTeamName",
      "hrTeamSignature",
      "financeTeamName",
      "financeTeamSignature",
      "headManufacturing",
      "headManufacturingSignature",
      "approvedDate",
      // Page 6
      "rentedBuilding",
      "rentedBuildingRemarks",
      "ownBuildingOwnership",
      "ownBuildingOwnershipRemarks",
      "companyRegistrationCertificate",
      "companyRegistrationCertificateRemarks",
      "factoryLicence",
      "factoryLicenceRemarks",
      "fireCertificate",
      "fireCertificateRemarks",
      "epfCodeRegistration",
      "epfCodeRegistrationRemarks",
      "esiCodeRegistration",
      "esiCodeRegistrationRemarks",
      "panCard",
      "panCardRemarks",
      "aadharCard",
      "aadharCardRemarks",
      "factoryBankDetails",
      "factoryBankDetailsRemarks",
      "listOfMachinery",
      "listOfMachineryRemarks",
      "polutionCentrolBoard",
      "polutionCentrolBoardRemarks",

      // ATTACHMENTS (MISSING BEFORE)
      // "rentalCopy_attachment",
      // "ownershipDetails_attachment",
      // "companyRegistration_attachment",
      // "factoryLicenceDoc_attachment",
      // "fireCertificate_attachment",
      // "epfRegistration_attachment",
      // "esiRegistration_attachment",
      // "panCard_attachment",
      // "udyogAadhar_attachment",
      // "factoryBankDetails_attachment",
      // "listOfMachinery_attachment",
      // "polutionControlBoard_attachment",
    ],
    // Step 3 - Digital Images (18 images)
    [
      "factoryNameBoard",
      "factoryPictureFull",
      "factoryEntrance",
      "productionFloorPic1",
      "productionFloorPic2",
      "productionFloorPic3",
      "productionFloorPic4",
      "productionFloorPic5",
      "productionFloorPic6",
      "storageSectionPic1",
      "storageSectionPic2",
      "storageSectionPic3",
      "firefightingEquipmentPic1",
      "firefightingEquipmentPic2",
      "firefightingEquipmentPic3",
      "othersPic1",
      "othersPic2",
      "othersPic3",
    ],
    // Step 3 - Additional details (no strict required fields)
    [],
  ];

  const handleSubmitApi = async (values, resetForm) => {
    setLoading(true);

    // Log all form data when Submit is clicked (check if all data is saved)
    console.log("=== Vendor Evaluation Form - Submit clicked ===");
    console.log("All form values:", values);
    // Summary: which fields have values (useful for file/image fields)
    const filledKeys = Object.keys(values).filter(
      (k) => values[k] !== null && values[k] !== undefined && values[k] !== "",
    );
    console.log("Fields with values (" + filledKeys.length + "):", filledKeys);
    // Image/file fields summary (File objects don't stringify well)
    const imageKeys = [
      "factoryNameBoard",
      "factoryPictureFull",
      "factoryEntrance",
      "productionFloorPic1",
      "productionFloorPic2",
      "productionFloorPic3",
      "productionFloorPic4",
      "productionFloorPic5",
      "productionFloorPic6",
      "storageSectionPic1",
      "storageSectionPic2",
      "storageSectionPic3",
      "firefightingEquipmentPic1",
      "firefightingEquipmentPic2",
      "firefightingEquipmentPic3",
      "othersPic1",
      "othersPic2",
      "othersPic3",
    ];
    const uploadedImages = imageKeys.filter(
      (k) => values[k] && (values[k].name || values[k] instanceof File),
    );
    console.log(
      "Uploaded images (" + uploadedImages.length + "):",
      uploadedImages.map((k) => ({ key: k, name: values[k]?.name })),
    );

    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      console.log("Form Data Submitted:", values);
      toast.success("Form submitted successfully!");
      setTimeout(() => {
        resetForm();
        setActiveStep(0);
      }, 2000);
    } catch (error) {
      toast.error("Error submitting form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const [fileArray, setFileArray] = useState([]);

  const uploadFilesToServer = async (vendorEvaluationId, fileArray = []) => {
    console.log(fileArray, "fileArray");
    for (const item of fileArray) {
      const { file, docKey } = item;

      if (file && file instanceof File) {
        await uploadVendorEvaluationFile(
          vendorEvaluationId,
          docKey, // type
          file, // original file
        );
      }
    }
  };

  // ADD FILE
  const handleAddFile = (docKey, file) => {
    setFileArray((prev) => {
      const filtered = prev.filter((f) => f.docKey !== docKey);

      return [
        ...filtered,
        {
          docKey,
          file,
          vendorEvaluationId,
        },
      ];
    });
  };

  // REMOVE FILE
  const handleRemoveFile = (docKey) => {
    setFileArray((prev) => prev.filter((f) => f.docKey !== docKey));
  };

  const noDataFound =
    isEdit && !loading && selectedForEdit === null && vendorEvaluationListId;

  if (noDataFound) {
    return (
      <Fragment>
        <Breadcrumb
          title="Vendor Evaluation Form"
          parent={
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                color="secondary"
                variant="contained"
                type="button"
                startIcon={<ArrowBack />}
                onClick={() =>
                  navigate(
                    `${process.env.PUBLIC_URL}/master/vendorEvaluationList`,
                  )
                }
                size="small"
              >
                Back to List
              </Button>
            </div>
          }
        />
        <Container fluid={true}>
          <Card className={classes.card}>
            <CardContent>
              <Typography
                variant="h6"
                color="textSecondary"
                align="center"
                sx={{ py: 4 }}
              >
                No Vendor Evaluation Details found
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      {loading && <CommonLoader />}
      <Breadcrumb
        title="Vendor Evaluation Form"
        parent={
          //onCancel ? (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              //variant="outlined"
              color={"secondary"}
              variant="contained"
              type="button"
              startIcon={<ArrowBack />}
              onClick={() => navigate(-1)}
              size="small"
            >
              Back to List
            </Button>
          </div>
          // ) : null
        }
      />
      <Box ref={stepperRef} className={classes.stickyStepper}>
        <Stepper activeStep={activeStep} alternativeLabel nonLinear>
          {steps.map((label, index) => (
            <Step key={label} disabled={false}>
              {maxStepReached >= 2 && index <= maxStepReached ? (
                <StepButton onClick={() => setActiveStep(index)}>
                  {label}
                </StepButton>
              ) : (
                <StepLabel>{label}</StepLabel>
              )}
            </Step>
          ))}
        </Stepper>
      </Box>
      <Container fluid={true}>
        <Card className={classes.card}>
          <CardContent>
            <div className={classes.root}>
              <Formik
                key={selectedForEdit?.vendorEvaluationId ?? "new"}
                initialValues={
                  selectedForEdit
                    ? mapApiResponseToFormValues(selectedForEdit, initialValues)
                    : initialValues
                }
                validationSchema={getValidationSchema(
                  selectedForEdit?.panNo ?? selectedForEdit?.pan_no,
                )}
                enableReinitialize
                validateOnChange={false}
                validateOnBlur={true}
                onSubmit={(values, { resetForm }) =>
                  handleSubmitApi(values, resetForm)
                }
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  setFieldValue,
                  setFieldError,
                  validateForm,
                  setTouched,
                  resetForm,
                }) => {
                  const handlePanBlur = (e) => {
                    handleBlur(e);
                    const pan = values.panNo;
                    if (pan == null || String(pan).trim() === "") return;
                    const panTrimmed = String(pan).trim();
                    // In edit mode, if user reverted to same PAN as loaded from getVendorEvaluationById, skip API call
                    const originalPan =
                      selectedForEdit?.panNo ?? selectedForEdit?.pan_no ?? "";
                    const originalPanTrimmed =
                      originalPan != null ? String(originalPan).trim() : "";
                    if (
                      vendorEvaluationId != null &&
                      vendorEvaluationId !== "" &&
                      originalPanTrimmed !== "" &&
                      panTrimmed === originalPanTrimmed
                    ) {
                      setFieldError("panNo", undefined);
                      return;
                    }
                    checkVendorEvaluationPanNoExists(
                      panTrimmed,
                      vendorEvaluationId,
                    )
                      .then((res) => {
                        setFieldError(
                          "panNo",
                          res.exists ? "Pan No already exist" : undefined,
                        );
                      })
                      .catch(() => setFieldError("panNo", undefined));
                  };
                  const handleNext = async () => {
                    const allErrors = await validateForm();
                    const stepFields = stepFieldGroups[activeStep] || [];
                    const stepErrorsKeys = Object.keys(allErrors).filter(
                      (key) => stepFields.includes(key),
                    );

                    if (stepErrorsKeys.length > 0) {
                      const newTouched = { ...touched };
                      stepErrorsKeys.forEach((key) => {
                        newTouched[key] = true;
                      });
                      setTouched(newTouched);
                      toast.error(
                        "Please correct errors in this step before continuing.",
                      );
                      return;
                    }

                    // setLoading(true);
                    try {
                      // Steps 0 (Factory Details), 1 (Manufacturing Facility), 2 (Evaluation) -> save to Vendor Evaluation table
                      if (
                        activeStep === 0 ||
                        activeStep === 1 ||
                        activeStep === 2
                      ) {
                        // Always include ALL steps 0,1,2 in payload so backend is never sent partial data.
                        // Otherwise saving Step 1 only would omit Step 2 keys and backend can overwrite them with null.
                        const stepsToInclude = [];
                        for (let s = 0; s <= 2; s++) {
                          const fields = stepFieldGroups[s] || [];
                          stepsToInclude.push(...fields);
                        }
                        const payload = {};
                        stepsToInclude.forEach((key) => {
                          const val = values[key];
                          if (val !== undefined && val !== null) {
                            payload[key] = val;
                          }
                        });
                        // if (activeStep === 1) {
                        const machineryDetails =
                          buildMachineryAvailableDetailsJson(values);
                        payload.machineryAvailableDetailsJson =
                          JSON.stringify(machineryDetails);
                        // }
                        if (
                          vendorEvaluationId != null &&
                          vendorEvaluationId !== ""
                        ) {
                          payload.vendorEvaluationId = vendorEvaluationId;
                        }
                        payload.currentStep = activeStep + 1;
                        console.log(payload, "payload");

                        const res = await saveOrUpdateVendorEvaluation(payload);
                        console.log(res, "res");
                        const data =
                          res?.response?.responseObject ?? res?.response ?? {};
                        const id =
                          data.id ??
                          data.vendorEvaluationId ??
                          data.vendor_evaluation_id;

                        if (fileArray.length > 0) {
                          await uploadFilesToServer(
                            id || vendorEvaluationId,
                            fileArray,
                          );
                        }
                        if (id != null && id !== "") {
                          setVendorEvaluationId(id);
                        }
                      }
                      // Step 3 (Annexure Digital Images / Attachments) -> save to Vendor Evaluation File Details table
                      if (activeStep === 3) {
                        const formData = new FormData();
                        if (
                          vendorEvaluationId != null &&
                          vendorEvaluationId !== ""
                        ) {
                          formData.append(
                            "vendorEvaluationId",
                            vendorEvaluationId,
                          );
                        }
                        const fileFields = stepFieldGroups[3] || [];
                        fileFields.forEach((key) => {
                          const file = values[key];
                          if (
                            file &&
                            (file instanceof File ||
                              (file.name && file.size !== undefined))
                          ) {
                            formData.append(key, file);
                          }
                        });
                        // await saveOrUpdateVendorEvaluationFileDetails(formData);
                      }

                      const nextStep = activeStep + 1;
                      setActiveStep(nextStep);
                      setMaxStepReached((prev) => Math.max(prev, nextStep));
                      window.scrollTo({ top: 0, behavior: "instant" });
                      if (stepperRef.current) {
                        stepperRef.current.scrollIntoView({
                          behavior: "instant",
                          block: "start",
                        });
                      }
                      if (activeStep === 3) {
                        toast.success("Attachments saved successfully.");
                      } else {
                        toast.success("Details saved successfully.");
                      }
                    } catch (err) {
                      toast.error(
                        err?.response?.data?.message ||
                          "Failed to save. Please try again.",
                      );
                    } finally {
                      setLoading(false);
                    }
                  };

                  const handleBack = () => {
                    setActiveStep((prev) => prev - 1);
                    // Scroll to top immediately
                    window.scrollTo({ top: 0, behavior: "instant" });
                    if (stepperRef.current) {
                      stepperRef.current.scrollIntoView({
                        behavior: "instant",
                        block: "start",
                      });
                    }
                  };

                  const handleReset = () => {
                    resetForm();
                    setActiveStep(0);
                    setVendorEvaluationId(null);
                  };

                  const getStepContent = (step) => {
                    switch (step) {
                      case 0:
                        return (
                          <Step1FactoryEvaluation
                            formData={values}
                            onFieldChange={handleChange}
                            onPanBlur={handlePanBlur}
                            errors={errors}
                            touched={touched}
                            setFieldValue={setFieldValue}
                            disable={{
                              isFinanceTeam,
                              isHRTeam,
                              isQCTeam,
                              isAdmin,
                            }}
                          />
                        );
                      case 1:
                        return (
                          <Step2ManufacturingFacility
                            formData={values}
                            onFieldChange={handleChange}
                            errors={errors}
                            touched={touched}
                            setFieldValue={setFieldValue}
                            disable={{
                              isFinanceTeam,
                              isHRTeam,
                              isQCTeam,
                              isAdmin,
                            }}
                          />
                        );
                      case 2:
                        return (
                          <Step3AddressInfo
                            formData={values}
                            onFieldChange={handleChange}
                            setFieldValue={setFieldValue}
                            setFormData={(updater) => {
                              const next =
                                typeof updater === "function"
                                  ? updater(values)
                                  : updater;
                              Object.keys(next).forEach((key) =>
                                setFieldValue(key, next[key]),
                              );
                            }}
                            disable={{
                              isFinanceTeam,
                              isHRTeam,
                              isQCTeam,
                              isAdmin,
                            }}
                            fileArray={fileArray}
                            handleAddFile={handleAddFile}
                            handleRemoveFile={handleRemoveFile}
                          />
                        );
                      case 3:
                        return (
                          <Step4AdditionalDetails
                            formData={values}
                            setFieldValue={setFieldValue}
                            disable={{
                              isFinanceTeam,
                              isHRTeam,
                              isQCTeam,
                              isAdmin,
                            }}
                          />
                        );
                      case 4:
                        return <Step5Review formData={values} />;
                      default:
                        return "Unknown step";
                    }
                  };

                  const handleSubmit = (e) => {
                    e.preventDefault();
                    // Only allow submit when user explicitly clicks Submit button (handled by that button's onClick)
                    // This prevents Enter key or other triggers from auto-submitting on Annexure step
                  };

                  return (
                    <Form onSubmit={handleSubmit}>
                      <div className={classes.stepContent}>
                        {activeStep === steps.length ? (
                          <Box>
                            <Typography>All steps completed</Typography>
                            <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
                              Reset
                            </Button>
                          </Box>
                        ) : (
                          <Box>
                            {getStepContent(activeStep)}
                            <Box className={classes.buttonContainer}>
                              {activeStep !== steps.length - 1 && (
                                <Button
                                  disabled={activeStep === 0}
                                  onClick={handleBack}
                                  sx={{ mr: 1 }}
                                  type="button"
                                >
                                  Back
                                </Button>
                              )}
                              <Box>
                                {activeStep === steps.length - 1 ? (
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    disabled={loading}
                                    onClick={() => handleSubmit()}
                                  >
                                    {loading ? "Submitting..." : "Submit"}
                                  </Button>
                                ) : (
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleNext}
                                    type="button"
                                  >
                                    Next
                                  </Button>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </CardContent>
        </Card>
      </Container>
      <ToastContainer autoClose={2000} />
    </Fragment>
  );
};

export default VendorEvaluationForm;
